import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../components/ui/VehicleServiceHero';
import { CheckCircle, AlertTriangle, Wrench, Settings, Clock, DollarSign, Phone, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const ClutchRepair = () => {
  const clutchFeatures = [
    'Precision Diagnostics',
    'European Clutch Specialists',
    'OEM & Performance Parts',
    'Dual-Mass Flywheel Experts',
    '12-Month/12k Mile Warranty',
    'Track-Ready Upgrades'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const warningSignals = [
    { title: 'Slipping Engagement', desc: 'RPM increases without corresponding acceleration' },
    { title: 'Difficult Shifts', desc: 'Resistance or grinding when changing gears' },
    { title: 'Vibration/Chatter', desc: 'Shuddering sensation during engagement' },
    { title: 'High Pedal Point', desc: 'Clutch engages near the top of travel' },
    { title: 'Burning Odor', desc: 'Distinctive smell during slip conditions' },
    { title: 'Pedal Anomalies', desc: 'Inconsistent feel or resistance changes' },
    { title: 'Unusual Sounds', desc: 'Clicking, squealing during operation' },
    { title: 'Fluid Evidence', desc: 'Hydraulic fluid near master/slave cylinders' }
  ];

  const vehicleProblems = [
    { 
      model: 'BMW 335i (E90/E92)', 
      years: '2007-2013',
      issue: 'Twin-turbo stress, pressure plate weakness',
      solution: 'Factory clutches often struggle with modified power levels. We offer reinforced solutions for optimal performance.',
      image: '/assets/assets/images/new/clutch/dual-mass-flywheel-european-car-repair-dallas.webp'
    },
    { 
      model: 'Volvo C30 T5', 
      years: '2007-2013',
      issue: 'DMF failure, clutch chatter, early wear',
      solution: 'Comprehensive drivetrain inspection recommended to address related wear patterns.',
      image: '/assets/assets/images/new/clutch/self-adjusting-clutch-european-auto-dallas.webp'
    },
    { 
      model: 'Mini Cooper S (R56)', 
      years: '2007-2013',
      issue: 'Rapid wear, DMF failure, costly replacement',
      solution: 'Single-mass flywheel conversions provide improved reliability and reduced maintenance costs.',
      image: '/assets/assets/images/new/clutch/single-mass-flywheel.webp'
    },
    {
      model: 'Volkswagen Jetta TDI',
      years: '2005-2015',
      issue: 'Torque-induced wear, DMF degradation',
      solution: 'High-torque diesel engines require upgraded clutch solutions for optimal durability.',
      image: '/assets/assets/images/new/clutch/self-adjusting-clutch-european-auto-dallas.webp'
    },
    {
      model: 'Porsche 911 (996/997)',
      years: '1999-2012',
      issue: 'RMS leaks, pressure plate failure',
      solution: 'Comprehensive approach addresses clutch issues while resolving related seal problems.',
      image: '/assets/assets/images/new/clutch/traditional-clutch.webp'
    },
    {
      model: 'BMW M3 (E46/E92)',
      years: '2001-2013',
      issue: 'Performance wear, SMG issues, DMF degradation',
      solution: 'Track-capable solutions available for both street and motorsport applications.',
      image: '/assets/assets/images/new/clutch/Hydraulic_Clutch_actuation.webp'
    },
    {
      model: 'Audi TT Quattro',
      years: '2000-2015',
      issue: 'AWD system stress, pressure plate weakness',
      solution: 'Quattro-specific solutions address unique loads and torque distribution requirements.',
      image: '/assets/assets/images/new/clutch/single-mass-flywheel.webp'
    },
    {
      model: 'Mercedes-Benz C-Class',
      years: '2001-2014',
      issue: 'Release bearing noise, heavy pedal feel',
      solution: 'Specialized service for rare manual transmission Mercedes models.',
      image: '/assets/assets/images/new/clutch/dual-mass-flywheel-european-car-repair-dallas.webp'
    }
  ];

  const services = [
    {
      title: 'Complete Clutch Replacement',
      items: ['Factory or performance clutch kits', 'Pressure plate and disc assembly', 'Release bearing and pilot bearing', 'Flywheel resurfacing or replacement', 'Hydraulic system inspection']
    },
    {
      title: 'Dual-Mass Flywheel Service',
      items: ['Failure diagnosis and replacement', 'Single-mass flywheel conversions', 'Lightweight flywheel upgrades', 'Vibration analysis and correction']
    },
    {
      title: 'Performance Upgrades',
      items: ['Stage 1-3 performance kits', 'Ceramic and carbon fiber options', 'Increased clamping force systems', 'Track and motorsport applications']
    },
    {
      title: 'Hydraulic System Repair',
      items: ['Master cylinder replacement', 'Slave cylinder rebuild/replacement', 'Clutch line repair and bleeding', 'Pedal assembly refurbishment']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Auto Clutch Repair Experts Of DFW | European & Diesel Specialists | Paradise-Performance</title>
        <meta name="description" content="Precision clutch repair & replacement for BMW, Mercedes, Audi, Porsche & diesel vehicles in Dallas. Dual-mass flywheel experts, performance upgrades. Same-day diagnosis. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/clutch-repair" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "European Clutch Repair & Replacement",
            "provider": {
              "@type": "AutoRepair",
              "name": "Paradise-Performance",
              "telephone": "(817) 382-2316",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "305 Co Rd 3470",
                "addressLocality": "Paradise",
                "addressRegion": "TX",
                "postalCode": "76073"
              }
            },
            "areaServed": "Dallas-Fort Worth",
            "description": "Expert clutch repair and replacement for European and diesel vehicles"
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Clutch Service"
        brandColor="#CC0000"
        tagline="Auto Clutch Repair & Replacement"
        description="Precision diagnostics for problematic diesel & European transmissions. Serving Dallas with expert replacement for high-performance manual transmissions."
        features={clutchFeatures}
        heroImage="/assets/assets/images/new/clutch/traditional-clutch.webp"
        heroImageAlt="Professional clutch repair service"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Premier Service Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Premier European Clutch Service</h2>
            <p className="text-lg text-gray-700 mb-4">
              At Paradise-Performance, we understand that European vehicles demand a higher caliber of service. 
              Our precision-trained technicians bring decades of specialized experience to every clutch repair, 
              ensuring your investment receives the attention it deserves.
            </p>
            <p className="text-lg text-gray-700">
              Whether you're experiencing subtle engagement issues or require a complete performance upgrade, 
              our facility is equipped with the latest diagnostic technology and OEM-quality tools specifically 
              designed for European drivetrains.
            </p>
          </motion.div>

          {/* Warning Signs */}
          <motion.div {...fadeInUp} className="bg-red-50 border-l-4 border-red-600 p-8 rounded-r-lg mb-16">
            <h2 className="text-3xl font-bold text-red-800 mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3" />
              Critical Clutch Warning Signs
            </h2>
            <p className="text-gray-700 mb-6">Professional attention at the first sign of clutch problems can prevent extensive damage:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {warningSignals.map((signal, index) => (
                <div key={index} className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-gray-900">{signal.title}:</span>
                    <span className="text-gray-700 ml-2">{signal.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Vehicle Problems */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">European Vehicles Requiring Specialized Clutch Service</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our expertise extends across Europe's premium manual transmission vehicles. These models frequently 
              present unique challenges that demand specialized knowledge and precision solutions:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicleProblems.map((vehicle, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{vehicle.model}</h3>
                    <p className="text-sm text-gray-600 mb-3">Years: {vehicle.years}</p>
                    <div className="mb-3">
                      <span className="text-sm font-semibold text-red-600">Common Issues:</span>
                      <p className="text-sm text-gray-700 mt-1">{vehicle.issue}</p>
                    </div>
                    <div className="pt-3 border-t">
                      <p className="text-sm text-gray-700">{vehicle.solution}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-8 mb-16">
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Is Your European Vehicle Experiencing Clutch Issues?</h3>
              <p className="text-xl mb-8 text-gray-300">
                Don't wait until minor symptoms become major failures. Our comprehensive diagnostic process 
                identifies issues before they escalate, saving you time and preserving your vehicle's performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:8173822316" className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                  Call (817) 382-2316
                </a>
                <Link to="/contact" className="inline-block px-8 py-4 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors">
                  Schedule Service Online
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Service Process */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Precision Service Process</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { num: 1, title: 'Comprehensive Diagnostic', desc: 'Complete assessment including road test and system inspection' },
                { num: 2, title: 'Technical Analysis', desc: 'Detailed examination of clutch components and hydraulic systems' },
                { num: 3, title: 'Custom Solution Design', desc: 'Parts selection based on vehicle use and driver preference' },
                { num: 4, title: 'Precision Installation', desc: 'Expert replacement following manufacturer procedures' },
                { num: 5, title: 'Quality Verification', desc: 'Comprehensive road testing and adjustment' },
                { num: 6, title: 'Long-Term Guidance', desc: 'Break-in procedures and maintenance recommendations' }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                  className="relative bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="absolute -top-3 left-4 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-bold mb-2 mt-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Specialized Clutch Services</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-red-600"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Expert Answers to Your Questions</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How long should a clutch last in a European vehicle?</h3>
                <p className="text-gray-700">
                  Under typical driving conditions, expect 60,000-100,000 miles from OEM components. Variables include 
                  driving style, traffic patterns, power modifications, and maintenance. Conservative driving can extend 
                  life beyond 100,000 miles.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">What causes premature clutch wear in European cars?</h3>
                <p className="text-gray-700">
                  Common factors include aggressive driving techniques, heavy stop-and-go traffic, improper towing practices, 
                  and power modifications without proper upgrades. European turbocharged engines create particular challenges 
                  due to high torque output.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Should I replace the flywheel during clutch service?</h3>
                <p className="text-gray-700">
                  Dual-mass flywheels typically require replacement with clutch service to ensure proper function and prevent 
                  premature failure. Single-mass flywheels can often be resurfaced if they meet specifications.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Can I drive with a slipping clutch?</h3>
                <p className="text-gray-700">
                  While short distances are possible, continued operation accelerates wear and can damage the flywheel, 
                  leading to more extensive repairs. Immediate diagnosis is recommended when slip symptoms appear.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div {...fadeInUp} className="bg-gray-100 rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Paradise-Performance?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'European drivetrain specialists with decades of experience',
                'Advanced diagnostic equipment for all European makes',
                'Access to OEM and premium aftermarket components',
                'Transparent pricing and detailed service reports',
                'Comprehensive warranty on parts and labor',
                'Expert guidance for optimization and upgrades'
              ].map((item, index) => (
                <div key={index} className="flex items-start">
                  <Wrench className="w-6 h-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Restore Your Clutch Performance?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Trust your European vehicle to technicians who understand precision engineering.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8173822316" className="inline-block px-8 py-4 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors">
                Call (817) 382-2316
              </a>
              <Link to="/contact" className="inline-block px-8 py-4 bg-gray-800 text-white font-bold rounded-lg hover:bg-gray-900 transition-colors">
                Request Service Quote
              </Link>
            </div>
            
            <div className="mt-12">
              <p className="text-gray-600">
                Also servicing: {' '}
                <Link to="/services/transmission-repair" className="text-blue-600 hover:underline">Transmission Repair</Link>, {' '}
                <Link to="/services/diagnostic-check-engine-light" className="text-blue-600 hover:underline">Check Engine Light</Link>, {' '}
                <Link to="/services/european-car-repair" className="text-blue-600 hover:underline">All European Vehicles</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ClutchRepair;
